import { useLocation } from "react-router";
import { Tabs, Tab, Button } from "@heroui/react";
import { GearIcon, HouseIcon, ListChecksIcon, NoteIcon } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

import { ICON_SIZE, ROUTES_TO } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { cloneElement } from "react";

export function BottomNavigationDesktop() {
  const location = useLocation();
  const fab = useBoundStore((state) => state.pageLayout.fab);
  const show = useBoundStore((state) => state.pageLayout.showBottomNavigation);
  const iconElement = fab
    ? cloneElement(fab.icon, {
        size: ICON_SIZE.lg,
        weight: "bold",
      })
    : null;

  const getActiveTab = () => {
    if (location.pathname.startsWith("/notes")) return "notes";
    if (location.pathname.startsWith(ROUTES_TO.todos)) return "todos";
    if (location.pathname.startsWith(ROUTES_TO.settings)) return "settings";
    return "home";
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed bottom-4 left-1/2 z-50 flex w-full max-w-5xl -translate-x-1/2 items-center justify-center gap-4"
          initial={{ translateY: "100%", opacity: 0 }}
          animate={{ translateY: "0%", opacity: 1, translateX: fab ? "-24px" : "0px" }}
          exit={{ translateY: "100%", opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {/* Navigation Bar - shifts left when FAB is visible */}
          <div className="border-default-200 dark:border-default-100 bg-background/80 relative flex items-center justify-around gap-0 rounded-full border p-1 backdrop-blur">
            <Tabs
              selectedKey={getActiveTab()}
              classNames={{
                tabList: "bg-transparent",
                tab: clsx("px-4 h-10 w-28", "data-[selected]:bg-content2"),
                cursor: "bg-content2!",
              }}
              radius="full"
            >
              <Tab
                key="home"
                title={
                  <div className="flex items-center gap-2">
                    <HouseIcon size={ICON_SIZE.md} />
                    <span>Home</span>
                  </div>
                }
                href={ROUTES_TO.home}
                routerOptions={{ replace: true }}
              />
              <Tab
                key="notes"
                title={
                  <div className="flex items-center gap-2">
                    <NoteIcon size={ICON_SIZE.md} />
                    <span>Notes</span>
                  </div>
                }
                href={ROUTES_TO.notes}
                routerOptions={{ replace: true }}
              />
              <Tab
                key="todos"
                title={
                  <div className="flex items-center gap-2">
                    <ListChecksIcon size={ICON_SIZE.md} />
                    <span>Todos</span>
                  </div>
                }
                href={ROUTES_TO.todos}
                routerOptions={{ replace: true }}
              />
              <Tab
                key="settings"
                title={
                  <div className="flex items-center gap-2">
                    <GearIcon size={ICON_SIZE.md} />
                    <span>Settings</span>
                  </div>
                }
                href={ROUTES_TO.settings}
                routerOptions={{ replace: true }}
              />
            </Tabs>
            {/* Floating Add Button - animates in/out with scale and opacity */}
            <AnimatePresence>
              {fab && (
                <motion.div
                  className="absolute -right-14 bottom-1"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Button
                    className="font-semibold"
                    color="primary"
                    isIconOnly
                    radius="full"
                    size="lg"
                    onPress={fab.onPress}
                  >
                    {iconElement}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
